import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
    PieChart, Pie, Cell,
} from "recharts";
import { Sparkles, ChevronRight, AlertTriangle, TrendingUp, Activity } from "lucide-react";
import { api } from "@/lib/api";
import KpiCard from "@/components/KpiCard";
import { DASH } from "@/constants/testIds";
import ExportBar from "@/components/ExportBar";

const HEALTH_COLORS = { Healthy: "#10B981", Monitor: "#3B82F6", "At Risk": "#F59E0B", Critical: "#EF4444" };

function fmtMoney(n) {
    if (!n && n !== 0) return "—";
    if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
    if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
    if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}k`;
    return `$${n}`;
}

export default function Dashboard() {
    const [data, setData] = useState(null);
    const [brief, setBrief] = useState("");
    const [briefBusy, setBriefBusy] = useState(false);

    useEffect(() => {
        api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => toast.error("Failed to load dashboard"));
    }, []);

    const generateBrief = async () => {
        setBriefBusy(true);
        setBrief("");
        try {
            const { data: r } = await api.post("/ai/task", { kind: "executive_brief" });
            setBrief(r.result);
            toast.success("Executive Morning Brief ready");
        } catch (e) {
            toast.error("AI task failed. Check API key balance.");
        } finally {
            setBriefBusy(false);
        }
    };

    if (!data) {
        return <div className="kicker text-slate-500">Loading executive dashboard…</div>;
    }

    const { kpis, cost_trend, project_health_distribution, projects_snapshot, recent_activity } = data;

    return (
        <div data-testid={DASH.root} className="space-y-8">
            {/* Header */}
            <div className="flex items-end justify-between gap-6 flex-wrap">
                <div>
                    <div className="kicker text-[var(--sgsb-gold)]">Portfolio · Feb 2026</div>
                    <h1 className="mt-2 font-display font-black text-white text-4xl md:text-5xl tracking-tighter">
                        Executive Dashboard
                    </h1>
                    <p className="mt-2 text-slate-400 max-w-2xl font-body text-sm">
                        Real-time performance across SGS Bateman&rsquo;s mining, metals, energy and infrastructure portfolio.
                    </p>
                </div>
                <button
                    data-testid={DASH.generateBrief}
                    onClick={generateBrief}
                    disabled={briefBusy}
                    className="inline-flex items-center gap-2 rounded-md bg-[var(--sgsb-gold)] text-slate-950 px-4 py-2.5 font-display font-bold text-sm hover:bg-[color:var(--sgsb-gold-soft)] disabled:opacity-60 transition-colors duration-200"
                >
                    <Sparkles className="h-4 w-4" strokeWidth={2} />
                    {briefBusy ? "Generating Brief…" : "Generate Executive Brief"}
                </button>
            </div>

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-4">
                <KpiCard label="Active Projects" value={kpis.active_projects} sub={`of ${kpis.total_projects} portfolio`} trend={3} tid="active-projects" />
                <KpiCard label="Portfolio Value" value={fmtMoney(kpis.total_contract_value_usd)} sub="Contracts under management" trend={4} tid="portfolio-value" />
                <KpiCard label="Portfolio CPI" value={kpis.portfolio_cpi.toFixed(2)} sub={kpis.portfolio_cpi >= 1 ? "On/under budget" : "Over budget"} trend={kpis.portfolio_cpi >= 1 ? 2 : -2} tid="portfolio-cpi" />
                <KpiCard label="Portfolio SPI" value={kpis.portfolio_spi.toFixed(2)} sub={kpis.portfolio_spi >= 1 ? "On schedule" : "Schedule slippage"} trend={kpis.portfolio_spi >= 1 ? 1 : -3} tid="portfolio-spi" />
                <KpiCard label="Open Tenders" value={kpis.open_tenders} sub={`${kpis.overdue_deliverables} overdue deliverables`} trend={0} tid="open-tenders" />
            </div>

            {/* Middle: Trend + Health */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass rounded-xl p-6 xl:col-span-2">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="kicker text-slate-400">Cost performance</div>
                            <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">
                                Planned · Actual · Forecast ($M)
                            </h3>
                        </div>
                        <div className="flex items-center gap-1.5 text-emerald-400 text-xs font-body">
                            <TrendingUp className="h-3 w-3" strokeWidth={2} />
                            <span>Trend within tolerance</span>
                        </div>
                    </div>
                    <div className="h-[300px] mt-5 -ml-3">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={cost_trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#D4AF37" stopOpacity={0.55} />
                                        <stop offset="100%" stopColor="#D4AF37" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="g2" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.5} />
                                        <stop offset="100%" stopColor="#3B82F6" stopOpacity={0} />
                                    </linearGradient>
                                    <linearGradient id="g3" x1="0" x2="0" y1="0" y2="1">
                                        <stop offset="0%" stopColor="#94A3B8" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#94A3B8" stopOpacity={0} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 6" stroke="rgba(148,163,184,0.12)" vertical={false} />
                                <XAxis dataKey="month" stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <YAxis stroke="#64748B" tick={{ fontSize: 11, fontFamily: "Manrope" }} />
                                <Tooltip
                                    contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }}
                                    labelStyle={{ color: "#94A3B8", textTransform: "uppercase", fontSize: 11, letterSpacing: 2 }}
                                />
                                <Area dataKey="planned" stroke="#94A3B8" strokeWidth={1.5} fill="url(#g3)" />
                                <Area dataKey="actual" stroke="#D4AF37" strokeWidth={2.4} fill="url(#g1)" />
                                <Area dataKey="forecast" stroke="#3B82F6" strokeWidth={1.5} strokeDasharray="4 4" fill="url(#g2)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="glass rounded-xl p-6">
                    <div className="kicker text-slate-400">Portfolio health</div>
                    <h3 className="font-display font-black text-white text-xl mt-1 tracking-tight">
                        Project distribution
                    </h3>
                    <div className="h-[220px] mt-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={project_health_distribution} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={3} strokeWidth={0}>
                                    {project_health_distribution.map((entry) => (
                                        <Cell key={entry.name} fill={HEALTH_COLORS[entry.name] || "#64748B"} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    contentStyle={{ background: "#0F172A", border: "1px solid rgba(212,175,55,0.35)", borderRadius: 8, color: "#F8FAFC" }}
                                />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 text-xs font-body">
                        {project_health_distribution.map((h) => (
                            <div key={h.name} className="flex items-center gap-2">
                                <span className="h-2 w-2 rounded-sm" style={{ background: HEALTH_COLORS[h.name] }} />
                                <span className="text-slate-400">{h.name}</span>
                                <span className="ml-auto text-slate-200 font-medium">{h.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Executive brief output */}
            {brief && (
                <div data-testid={DASH.briefOutput} className="glass rounded-xl p-6 relative">
                    <div className="absolute top-0 left-6 -translate-y-1/2 px-3 py-1 rounded-full bg-[var(--sgsb-gold)] text-slate-950 kicker text-[10px]">
                        Executive Morning Brief · GPT-5.2
                    </div>
                    <pre className="whitespace-pre-wrap text-slate-200 font-body text-sm leading-relaxed">{brief}</pre>
                    <ExportBar
                        title="Executive Morning Brief"
                        content={brief}
                        confidentiality="Executive"
                        testIdPrefix="brief-export"
                    />
                </div>
            )}

            {/* Bottom: Projects snapshot + Activity */}
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
                <div className="glass rounded-xl xl:col-span-2 overflow-hidden">
                    <div className="p-5 flex items-center justify-between border-b border-slate-800/60">
                        <div>
                            <div className="kicker text-slate-400">Portfolio</div>
                            <h3 className="font-display font-black text-white text-lg mt-1 tracking-tight">Projects snapshot</h3>
                        </div>
                        <Link to="/projects" className="text-xs text-[var(--sgsb-gold)] inline-flex items-center gap-1 hover:underline">
                            View all <ChevronRight className="h-3 w-3" />
                        </Link>
                    </div>
                    <table className="w-full text-sm font-body">
                        <thead>
                            <tr className="text-slate-500 kicker text-[10px]">
                                <th className="text-left px-5 py-3">Project</th>
                                <th className="text-left px-3 py-3">Health</th>
                                <th className="text-left px-3 py-3">Progress</th>
                                <th className="text-left px-3 py-3">CPI</th>
                                <th className="text-left px-3 py-3">SPI</th>
                                <th className="text-right px-5 py-3">Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects_snapshot.map((p) => (
                                <tr key={p.id} className="border-t border-slate-800/60 hover:bg-slate-900/40 transition-colors duration-150">
                                    <td className="px-5 py-3">
                                        <Link to={`/projects/${p.id}`} className="text-slate-100 hover:text-[var(--sgsb-gold)]">
                                            <div className="font-medium">{p.name}</div>
                                            <div className="text-[11px] text-slate-500">{p.code} · {p.location}</div>
                                        </Link>
                                    </td>
                                    <td className="px-3 py-3">
                                        <span
                                            className="inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-[11px]"
                                            style={{
                                                background: `${HEALTH_COLORS[p.health]}22`,
                                                color: HEALTH_COLORS[p.health],
                                                border: `1px solid ${HEALTH_COLORS[p.health]}55`,
                                            }}
                                        >
                                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: HEALTH_COLORS[p.health] }} />
                                            {p.health}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3">
                                        <div className="flex items-center gap-2 min-w-[120px]">
                                            <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                                                <div className="h-full bg-[var(--sgsb-gold)]" style={{ width: `${p.progress}%` }} />
                                            </div>
                                            <span className="text-slate-300 text-xs w-9 text-right">{p.progress}%</span>
                                        </div>
                                    </td>
                                    <td className={`px-3 py-3 font-medium ${p.cpi >= 1 ? "text-emerald-400" : "text-red-400"}`}>{p.cpi.toFixed(2)}</td>
                                    <td className={`px-3 py-3 font-medium ${p.spi >= 1 ? "text-emerald-400" : "text-red-400"}`}>{p.spi.toFixed(2)}</td>
                                    <td className="px-5 py-3 text-right text-slate-200">{fmtMoney(p.value_usd)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="glass rounded-xl p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="kicker text-slate-400">Signal</div>
                            <h3 className="font-display font-black text-white text-lg mt-1 tracking-tight">Recent activity</h3>
                        </div>
                        <Activity className="h-4 w-4 text-[var(--sgsb-gold)]" strokeWidth={1.5} />
                    </div>
                    <div className="mt-4 space-y-3">
                        {recent_activity.map((a, i) => (
                            <div key={i} className="p-3 rounded-md bg-slate-950/40 border border-slate-800 hover:border-[color:var(--sgsb-gold)]/40 transition-colors duration-200">
                                <div className="flex items-center gap-2 text-[11px] kicker text-[var(--sgsb-gold)]">
                                    <AlertTriangle className="h-3 w-3" strokeWidth={2} />
                                    {a.type}
                                </div>
                                <div className="mt-1 text-slate-100 text-sm font-body">{a.title}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">{a.detail} · {a.when}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
